import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api, { track } from "@/lib/api";
import {
  HandsPraying,
  BookOpen,
  Sparkle,
  Compass,
  ArrowRight,
  ArrowLeft,
  Check,
} from "@phosphor-icons/react";

const languages = ["English", "Spanish", "Portuguese", "French", "Korean", "Chinese", "Arabic", "Hindi", "Swahili", "German"];

const interests = [
  { id: "prayer", label: "Prayer" },
  { id: "bible", label: "Bible Study" },
  { id: "anxiety", label: "Peace & Anxiety" },
  { id: "grief", label: "Grief & Loss" },
  { id: "healing", label: "Healing" },
  { id: "family", label: "Family" },
  { id: "purpose", label: "Purpose" },
  { id: "discipleship", label: "Discipleship" },
  { id: "encouragement", label: "Encouragement" },
  { id: "worship", label: "Worship" },
];

// Support modes — replace bot-choice framing.
// Each maps to an underlying bot id so the rest of the product still works.
const supportModes = [
  {
    id: "prayer-support",
    bot: "prayerbot",
    title: "Prayer Support",
    desc: "Guided prayer for what you're facing",
    icon: HandsPraying,
  },
  {
    id: "scripture-guidance",
    bot: "counselor",
    title: "Scripture Guidance",
    desc: "Bible-based answers and reflection",
    icon: BookOpen,
  },
  {
    id: "daily-encouragement",
    bot: "companion",
    title: "Daily Encouragement",
    desc: "Faith reminders and spiritual support",
    icon: Sparkle,
  },
  {
    id: "platform-help",
    bot: "guide",
    title: "Platform Help",
    desc: "Learn how Voice works",
    icon: Compass,
  },
];

export default function OnboardingPage() {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [language, setLanguage] = useState("English");
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [supportMode, setSupportMode] = useState("prayer-support");
  const [loading, setLoading] = useState(false);

  const toggleInterest = (id) =>
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleFinish = async () => {
    setLoading(true);
    const chosen = supportModes.find((s) => s.id === supportMode);
    try {
      const res = await api.post("/onboarding", {
        language,
        interests: selectedInterests,
        goals: selectedInterests,
        preferred_bot: chosen?.bot || "prayerbot",
      });
      track("onboarding_completed", { language, interests_count: selectedInterests.length, support_mode: chosen?.id });
      updateUser(res.data.user);
      navigate("/home");
    } catch {
      navigate("/home");
    }
  };

  const totalSteps = 3;

  const stepHeader = (eyebrow, title, sub) => (
    <div className="mb-6">
      <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body mb-2">{eyebrow}</p>
      <h2 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] mb-2 font-medium">{title}</h2>
      <p className="text-[#8B7355] font-body text-sm">{sub}</p>
    </div>
  );

  const steps = [
    <div key="lang" className="animate-fade-in">
      {stepHeader("Step 1 of 3", "Choose Your Language", "Select the language you'd like to use with Voice.")}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {languages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            data-testid={`lang-${lang.toLowerCase()}`}
            className={`px-4 py-2.5 rounded-xl font-body text-sm transition-all ${
              language === lang
                ? "glass-card-strong text-[#5C4033] font-medium ring-1 ring-[#C4A265]"
                : "glass-card text-[#8B7355] hover:bg-white/50"
            }`}
          >
            {lang}
          </button>
        ))}
      </div>
    </div>,

    <div key="interests" className="animate-fade-in">
      {stepHeader(
        "Step 2 of 3",
        "What are you seeking today?",
        "Choose the areas where Voice can support your faith."
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {interests.map((item) => {
          const active = selectedInterests.includes(item.id);
          return (
            <button
              key={item.id}
              onClick={() => toggleInterest(item.id)}
              data-testid={`interest-${item.id}`}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-body text-sm transition-all ${
                active
                  ? "glass-card-strong text-[#5C4033] font-medium ring-1 ring-[#C4A265]"
                  : "glass-card text-[#8B7355]"
              }`}
            >
              {active && <Check size={14} className="text-[#C4A265]" />}
              {item.label}
            </button>
          );
        })}
      </div>
      {selectedInterests.length === 0 && (
        <p className="text-[#B8A088] text-xs font-body mt-3">Pick a few to personalize your experience.</p>
      )}
    </div>,

    <div key="support" className="animate-fade-in">
      {stepHeader(
        "Step 3 of 3",
        "How would you like to begin?",
        "Select the kind of support you want first."
      )}
      <div className="space-y-2">
        {supportModes.map((mode) => {
          const active = supportMode === mode.id;
          return (
            <button
              key={mode.id}
              onClick={() => setSupportMode(mode.id)}
              data-testid={`support-${mode.id}`}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl transition-all text-left ${
                active ? "glass-card-strong ring-1 ring-[#C4A265]" : "glass-card"
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${active ? "bg-[#FAF5EC]" : "bg-[#EDE6DC]"}`}>
                <mode.icon
                  size={22}
                  weight="duotone"
                  className={active ? "text-[#C4A265]" : "text-[#8B7355]"}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-body text-sm font-medium ${active ? "text-[#5C4033]" : "text-[#2D1B0E]"}`}>
                  {mode.title}
                </p>
                <p className="text-[#8B7355] font-body text-xs">{mode.desc}</p>
              </div>
              {active && <Check size={18} className="text-[#C4A265]" />}
            </button>
          );
        })}
      </div>
    </div>,
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <span className="font-heading tracking-[0.25em] text-2xl text-[#2D1B0E]">
            VO<span className="text-[#C4A265]">&#10013;</span>CE
          </span>
        </div>
        <Progress
          value={((step + 1) / totalSteps) * 100}
          className="h-1 mb-8 bg-[#EDE6DC]"
          data-testid="onboarding-progress"
        />
        <div className="glass-card-strong p-7" data-testid="onboarding-card">
          {steps[step]}
        </div>
        <div className="flex justify-between mt-5">
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="border-[#D4C9BA] text-[#5C4033] rounded-full px-5 font-body"
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          ) : (
            <div />
          )}
          {step < totalSteps - 1 ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-5 font-body"
              data-testid="onboarding-next-btn"
            >
              Continue <ArrowRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={loading}
              className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-5 font-body"
              data-testid="onboarding-finish-btn"
            >
              {loading ? "Setting up…" : "Start with Voice"} <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
        <p className="text-center text-[#B8A088] text-xs font-body mt-6">
          Scripture-grounded. Built with theological integrity. An extension of the Church—not a replacement.
        </p>
      </div>
    </div>
  );
}
